import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Button, Chip, CircularProgress, FormControlLabel, Grid, InputAdornment, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Lock, Search } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import { getMasters, lookupBankIfsc, type BankIfscLookup } from '../../services/platform/masterService';
import {
  BANK_FIELD_LABELS,
  IFSC_PATTERN,
  nextBankSortOrder,
  normalizeBankName,
  previewBankCode,
  sanitizeBic,
  sanitizeIfsc,
  sanitizeIfscPrefix,
  usesIfsc,
  validateBankForm,
  type BankExistingRecord,
} from '../../utils/bankMasterValidation';
import { countryDisplayName, countryFlag } from '../../utils/stateMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

interface CountryOption {
  code: string;
  name: string;
  isActive: boolean;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const lockAdornment = {
  readOnly: true,
  endAdornment: (
    <InputAdornment position="end">
      <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
    </InputAdornment>
  ),
};

export default function BankFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [nameEdited, setNameEdited] = useState(false);
  const [ifsc, setIfsc] = useState('');
  const [lookup, setLookup] = useState<BankIfscLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const country = str(form.country).toUpperCase();
  const meta = form.metadata;
  const international = meta.internationalTransfers === true;

  const { data: countriesRaw, isLoading: countriesLoading } = useQuery({
    queryKey: ['masters', 'countries', 'bank-form'],
    queryFn: () => getMasters('countries', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const countryOptions = useMemo<CountryOption[]>(
    () => ((countriesRaw?.data ?? []) as Array<{ code?: string; name?: string; isActive?: boolean }>)
      .map((c) => ({ code: str(c.code).toUpperCase(), name: str(c.name), isActive: c.isActive !== false }))
      .filter((c) => /^[A-Z]{2}$/.test(c.code))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [countriesRaw],
  );
  const selectedCountry = useMemo<CountryOption | null>(() => {
    if (!country) return null;
    return countryOptions.find((c) => c.code === country) || { code: country, name: countryDisplayName(country), isActive: true };
  }, [country, countryOptions]);

  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'banks', 'form-siblings'],
    queryFn: () => getMasters('banks', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 0,
  });
  const existing = useMemo<BankExistingRecord[]>(() => ((existingRaw?.data ?? []) as BankExistingRecord[]), [existingRaw]);
  const suggestedSortOrder = useMemo(() => nextBankSortOrder(existing, editingId), [existing, editingId]);
  const editingRow = existing.find((r) => r._id === editingId);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    setNameEdited(editing);
    setIfsc('');
    setLookup(null);
    setLookupError(null);
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', country: 'IN', isActive: true, sortOrder: 0, metadata: { ifscPrefix: '', internationalTransfers: false, swiftCode: '' } });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const formInput = { name: form.name, country, description: form.description, sortOrder: form.sortOrder, metadata: meta };
  const code = editing ? str(form.code).toUpperCase() : previewBankCode(formInput);
  const errors = useMemo(
    () => validateBankForm(formInput, { existing, editingId, code }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.name, country, form.description, form.sortOrder, meta, existing, editingId, code],
  );
  const blocking = Object.keys(errors).map((key) => [key, BANK_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const setMeta = (patch: Record<string, string | number | boolean>, extra: Partial<MasterFormState> = {}) => {
    onChange({ ...form, ...extra, metadata: { ...meta, ...patch } });
  };

  const applyLookup = (result: BankIfscLookup, overwriteName: boolean) => {
    const patch: Record<string, string | number | boolean> = { ifscPrefix: result.ifscPrefix };
    if (result.swiftCode) {
      patch.internationalTransfers = true;
      patch.swiftCode = result.swiftCode;
    }
    const extra: Partial<MasterFormState> = { country: 'IN' };
    if (result.bankName && (overwriteName || !nameEdited || !str(form.name))) {
      extra.name = normalizeBankName(result.bankName);
      setNameEdited(false);
    }
    setMeta(patch, extra);
    touch('ifscPrefix', 'name', ...(result.swiftCode ? ['swiftCode'] : []));
  };

  const runLookup = async (value: string) => {
    if (!IFSC_PATTERN.test(value)) {
      setLookupError('IFSC is 11 characters: 4 letters, a zero, then 6 letters or digits (e.g. HDFC0001234)');
      return;
    }
    setLookingUp(true);
    setLookupError(null);
    try {
      const result = await lookupBankIfsc(value);
      setLookup(result);
      if (!result.existing) applyLookup(result, false);
      if (!result.bankName) setLookupError('Bank not found for this IFSC — fill in the details manually.');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setLookupError(msg || 'Lookup failed — fill in the details manually.');
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  useEffect(() => {
    if (open && !editing && code !== str(form.code)) onChange({ ...form, code });
  }, [open, editing, code, form, onChange]);

  const actionLabel = editing ? 'Update' : 'Create';
  const ifscBank = usesIfsc(country);

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Bank`}
      subtitle="A bank partners and merchants can receive payouts in. Identifiers are validated against IFSC and SWIFT / BIC rules."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={660}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom>Complete these to enable {actionLabel}:</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {blocking.map(([key, label]) => (
              <Chip
                key={key}
                size="small"
                label={`${label}: ${errors[key]}`}
                color="warning"
                variant="outlined"
                onClick={() => focusField(key)}
                sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
              />
            ))}
          </Stack>
        </Alert>
      )}
      {editing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Country, IFSC prefix and code are locked so saved bank accounts stay linked.
          {editingRow?.deleteLock ? ` ${editingRow.deleteLock}.` : ''}
        </Alert>
      )}

      {!editing && (
        <FormSection title="Find by IFSC" divider>
          <FormGrid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Any branch IFSC (optional)"
                value={ifsc}
                placeholder="e.g. HDFC0001234"
                onChange={(e) => {
                  const value = sanitizeIfsc(e.target.value);
                  setIfsc(value);
                  setLookupError(null);
                  if (value.length === 11) void runLookup(value);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runLookup(ifsc); } }}
                helperText={lookupError || 'Letters and digits only · 11 characters · fills in the bank name, IFSC prefix and SWIFT — you can still edit them'}
                error={!!lookupError && !lookup?.bankName}
                InputProps={{
                  sx: { fontFamily: 'monospace' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" startIcon={lookingUp ? <CircularProgress size={14} /> : <Search />} disabled={lookingUp || ifsc.length !== 11} onClick={() => void runLookup(ifsc)}>
                        Look up
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {lookup?.bankName && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <div>
                      <Typography variant="body2" fontWeight={600}>{lookup.bankName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[lookup.branch, lookup.city, lookup.state].filter(Boolean).join(', ') || 'Branch details unavailable'}
                        {` · IFSC prefix ${lookup.ifscPrefix}`}
                        {lookup.swiftCode ? ` · SWIFT ${lookup.swiftCode}` : ' · no SWIFT published'}
                        {lookup.source === 'directory' ? ' · from the built-in bank list' : ''}
                      </Typography>
                    </div>
                    {!lookup.existing && nameEdited && normalizeBankName(lookup.bankName) !== normalizeBankName(form.name) && (
                      <Button size="small" onClick={() => applyLookup(lookup, true)}>Use this name</Button>
                    )}
                  </Stack>
                  {lookup.existing && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      This bank is already in the master as "{lookup.existing.name}"{lookup.existing.isActive ? '' : ' (inactive)'} — edit that record instead.
                    </Alert>
                  )}
                </Paper>
              </Grid>
            )}
          </FormGrid>
        </FormSection>
      )}

      <FormSection title="Basic info" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="country">
            <Autocomplete
              options={countryOptions}
              loading={countriesLoading}
              disabled={editing}
              value={selectedCountry}
              onChange={(_, value) => {
                const next = value?.code ?? '';
                setMeta(usesIfsc(next) ? {} : { ifscPrefix: '' }, { country: next });
                touch('country');
              }}
              onBlur={() => touch('country')}
              getOptionLabel={(o) => `${countryFlag(o.code)} ${o.name} (${o.code})`.trim()}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              getOptionDisabled={(o) => !o.isActive}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Country"
                  error={!!errorFor('country')}
                  helperText={errorFor('country') || (editing ? 'Locked' : 'Decides which identifiers apply — IFSC for India, SWIFT / BIC for international')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Bank name"
              value={form.name}
              onChange={(e) => {
                setNameEdited(true);
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeBankName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Unique within the country · letters only (e.g. HDFC Bank)'}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="code">
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={code}
              placeholder="Generated automatically"
              error={!!errors.code}
              helperText={errors.code || (ifscBank
                ? 'Auto — always the IFSC prefix (uppercase)'
                : international ? 'Auto — SWIFT bank code (first 4 letters of the BIC)' : 'Auto — from the bank name (uppercase)')}
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace', fontSize: 13 } }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Position in bank pickers · unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Banking identifiers">
        <FormGrid>
          {ifscBank && (
            <Grid item xs={12} sm={6} data-field="ifscPrefix">
              <TextField
                fullWidth
                size="small"
                required
                label="IFSC prefix"
                value={str(meta.ifscPrefix)}
                onChange={(e) => { setMeta({ ifscPrefix: sanitizeIfscPrefix(e.target.value) }); touch('ifscPrefix'); }}
                onBlur={() => touch('ifscPrefix')}
                error={!!errorFor('ifscPrefix')}
                helperText={errorFor('ifscPrefix') || (editing ? 'Locked' : 'First 4 letters of every branch IFSC · letters only (e.g. HDFC in HDFC0001234)')}
                InputProps={editing ? { ...lockAdornment, sx: { fontFamily: 'monospace' } } : { sx: { fontFamily: 'monospace' } }}
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={ifscBank ? 6 : 12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={international}
                  onChange={(e) => {
                    setMeta(e.target.checked ? { internationalTransfers: true } : { internationalTransfers: false, swiftCode: '' });
                    touch('swiftCode');
                  }}
                />
              )}
              label="Supports international transfers"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {international
                ? 'SWIFT / BIC is required for international (cross-border) payouts.'
                : 'Domestic only — SWIFT / BIC is marked not applicable.'}
            </Typography>
          </Grid>
          {international && (
            <Grid item xs={12} sm={6} data-field="swiftCode">
              <TextField
                fullWidth
                size="small"
                required
                label="SWIFT / BIC"
                value={str(meta.swiftCode)}
                onChange={(e) => { setMeta({ swiftCode: sanitizeBic(e.target.value) }); touch('swiftCode'); }}
                onBlur={() => touch('swiftCode')}
                error={!!errorFor('swiftCode')}
                helperText={errorFor('swiftCode') || `8 or 11 characters · bank (4) + country${country ? ` (${country})` : ''} + location (2) + optional branch (3) · e.g. HDFCINBB`}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Banks list to activate or deactivate this bank. Inactive banks cannot be chosen for new bank accounts.'
                : 'New banks are created as Active — use the status toggle in the list to stop new bank accounts with this bank.'}
            </Typography>
          </Grid>
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
